/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CloudModule
 */

import { IDataNetworkConfig, NsxtTransportZoneType } from 'generated-types';
import {
    MessageItem,
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories';
import { NsxtTier1SegmentConfigConfigItem, Tier1LogicalRouterInfoConfigItem } from '.';

type TDataNetworkConfigConfigPartial = Omit<IDataNetworkConfig, 'tier1_segment_config'>;

interface IDataNetworkConfigConfig extends TDataNetworkConfigConfigPartial {
    tier1_segment_config?: NsxtTier1SegmentConfigConfigItem;
}

export const DATA_NETWORK_CONFIG_CONFIG_ITEM_TOKEN = 'DataNetworkConfigConfigItem';

export class DataNetworkConfigConfigItem extends MessageItem<IDataNetworkConfigConfig> {
    /** @constructor */
    constructor(args = {}) {
        const extendedArgs = {
            objectType: 'DataNetworkConfig',
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Getter for data transport zone type overlay or vlan.
     * Return undefined if not configured.
     */
    public get transportZoneType(): NsxtTransportZoneType | undefined {
        return this.config.tz_type;
    }

    /**
     * Setter for transport zone type.
     */
    public set transportZoneType(type: NsxtTransportZoneType) {
        this.config.tz_type = type;
    }

    /**
     * Getter for the name of transport zone path for avi service engines.
     * Return undefined if not configured.
     */
    public get transportZone(): string | undefined {
        return this.config.transport_zone;
    }

    /**
     * Setter for transport zone.
     */
    public set transportZone(transportZone: string) {
        this.config.transport_zone = transportZone;
    }

    /**
     * Getter for tier1 segment config item.
     * Defined only when transport zone type is OVERLAY.
     */
    public get tier1SegmentConfig(): NsxtTier1SegmentConfigConfigItem | undefined {
        return this.config.tier1_segment_config;
    }

    /**
     * Getter for Tier1LogicalRouterInfo RepeatedMessageItem under manual mode of tier1_segment.
     * Defined only when transport zone type is OVERLAY.
     */
    public get manualTier1LRs(): RepeatedMessageItem<Tier1LogicalRouterInfoConfigItem> | undefined {
        return this.tier1SegmentConfig?.manualModeConfig.tier1LrsConfig;
    }

    /**
     * Check current transport zone type against the given one.
     */
    public isTransportZoneType(type: NsxtTransportZoneType): boolean {
        return this.transportZoneType === type;
    }

    /**
     * Remove the configured transport zone and related data.
     */
    public clearTransportZoneData(): void {
        this.config.transport_zone = undefined;
        this.config.tz_type = undefined;

        this.onTransportZoneChange();
    }

    /**
     * Set or remove fields when transport zone type changes.
     */
    public onTransportZoneChange(): void {
        this.resetFieldsByTranportZoneType();
    }

    /**
     * Set transport zone, tier1, and segment IDs.
     */
    public configureOverlayFields(tzId: string, tier1Id: string, segmentId: string): void {
        this.transportZone = tzId;

        this.resetFieldsByTranportZoneType();
        this.manualTier1LRs.add();
        this.manualTier1LRs.at(0).setTier1AndSegment(tier1Id, segmentId);
    }

    /**
     * Reset fields that are mutually exclusive based on transport zone type.
     */
    private resetFieldsByTranportZoneType(): void {
        const { tz_type: tzType } = this.config;

        switch (tzType) {
            case NsxtTransportZoneType.OVERLAY:
                if (this.tier1SegmentConfig) {
                    this.tier1SegmentConfig.removeManualTier1LRs();
                } else {
                    this.safeSetNewChildByField('tier1_segment_config');
                }

                delete this.config.vlan_segments;
                break;

            case NsxtTransportZoneType.VLAN:
                this.config.vlan_segments = [];
                delete this.config.tier1_segment_config;
                break;

            default:
                delete this.config.vlan_segments;
                delete this.config.tier1_segment_config;
        }
    }
}
